import { GetDraftPaymentItemsPaginatedDocument } from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'

const PER_PAGE = 25

/** Minimal shape used by getUserByItem and disablePaymentItem. DraftPaymentItemLike satisfies this; PaymentItem (mobx) is accepted via union where needed. */
export interface PaymentItemLikeMinimal {
  id: number
  attributes: {
    employee_id?: number
    amount?: number
    bank_account?: string
    userId?: number
    full_name?: string
  }
  get(key: string): unknown
  has(key: string): boolean
}

/** Shape for paginated draft payment rows; extends PaymentItemLikeMinimal and satisfies RecordType. */
export interface DraftPaymentItemLike extends PaymentItemLikeMinimal {
  id: number
  attributes: PaymentItemLikeMinimal['attributes'] & { paymentType: string }
  [key: string]: unknown
}

function nodeToPaymentItemLike(node: {
  employeeId: number
  amountInCents: number
  bankAccount?: string | null
  paymentType: string
}): DraftPaymentItemLike {
  const attrs: DraftPaymentItemLike['attributes'] = {
    employee_id: node.employeeId,
    amount: node.amountInCents,
    bank_account: node.bankAccount ?? undefined,
    paymentType: node.paymentType,
  }
  const id = node.employeeId
  const get = (key: string): unknown => {
    if (key === 'id') return id
    switch (key) {
      case 'employee_id':
        return attrs.employee_id
      case 'amount':
        return attrs.amount
      case 'bank_account':
        return attrs.bank_account
      case 'paymentType':
        return attrs.paymentType
      default:
        return undefined
    }
  }
  const has = (key: string): boolean => key === 'id' || key in attrs

  return {
    id,
    attributes: attrs,
    get,
    has,
  }
}

export function useDraftPaymentItemsDataCollectionSource(
  policyPeriodId: number,
  paymentType: string | null | undefined
) {
  return useGraphqlDataCollectionSource(
    {
      document: GetDraftPaymentItemsPaginatedDocument,
      perPage: PER_PAGE,
      paginationType: 'pages',
      mapVariables: () => ({
        policyPeriodId,
        paymentType: paymentType ?? undefined,
      }),
      mapPagination: (data) => ({
        total: data?.payroll?.draftPaymentItemsConnection?.totalCount ?? 0,
      }),
      mapData: (data) => {
        const nodes = data?.payroll?.draftPaymentItemsConnection?.nodes ?? []
        return nodes.map(
          (node: {
            employeeId: number
            amountInCents: number
            bankAccount?: string | null
            paymentType: string
          }) => nodeToPaymentItemLike(node)
        )
      },
      itemUrl: () => '',
      search: { enabled: false },
    },
    [policyPeriodId, paymentType]
  )
}
